'use client';

import React, { FC, useEffect, useRef, useState } from 'react';

import { ArrowPathIcon } from '@heroicons/react/24/outline';
import cn from 'classnames';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { InferType } from 'yup';

import Button from '@/components/button.component';
import Textarea from '@/components/textarea.component';
import SuccessToast from '@/components/toasts/success-toast.component';
import WarningToast from '@/components/toasts/warning-toast.component';

const schema = yup.object().shape({
  trait: yup.string().required(),
  industry: yup.string().required(),
  details: yup.string(),
});

type ChatbotNameGeneratorFormData = InferType<typeof schema>;

const ChatbotNameGenerator: FC<{ t: any }> = ({ t }) => {
  const [loading, setLoading] = useState(false);
  const [selectedTrait, setSelectedTrait] = useState<string | null>(null);
  const [selectedIndustry, setSelectedIndustry] = useState<string | null>(null);
  const [details, setDetails] = useState<string>('');
  const [chatbotNames, setChatbotNames] = useState<string[]>([]);
  const [displayedNames, setDisplayedNames] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();

  useEffect(() => {
    const onResize = (height: number) => {
      window.parent.postMessage({ height }, '*');
    };

    const resizeObserver = new ResizeObserver(() => {
      if (
        containerRef.current &&
        containerRef.current.offsetHeight !== height
      ) {
        setHeight(containerRef.current.offsetHeight);
        onResize(containerRef.current.offsetHeight);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, [containerRef, height]);

  const handleTraitSelection = (trait: string) => setSelectedTrait(trait);
  const handleIndustrySelection = (industry: string) =>
    setSelectedIndustry(industry);

  const validateAndSubmit = async (formData: ChatbotNameGeneratorFormData) => {
    try {
      await schema.validate(formData, { abortEarly: false });
      setLoading(true);

      const response = await fetch('/api/free-tools/chatbot-name-generator', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const responseData = await response.json();

      if (responseData.status === 'error') {
        throw new Error(responseData?.message || 'Something went wrong');
      }

      const names = responseData.names;
      setChatbotNames(names);
      setDisplayedNames(names.slice(0, 3));
      toast(<SuccessToast title={t.successTitle} detail={t.successDetail} />);
    } catch (err) {
      if (err.inner) {
        err.inner.forEach((validationError: yup.ValidationError) => {
          toast(
            <WarningToast
              title={t.validationFailTitle}
              detail={validationError.message}
            />,
          );
        });
      } else {
        toast(<WarningToast title={t.failTitle} detail={t.failDetail} />);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    const formData = {
      trait: selectedTrait,
      industry: selectedIndustry,
      details,
    };

    validateAndSubmit(formData as ChatbotNameGeneratorFormData);
  };

  const handleStartOver = () => {
    setSelectedTrait(null);
    setSelectedIndustry(null);
    setDetails('');
    setChatbotNames([]);
    setDisplayedNames([]);
  };

  const handleShowMeMore = () => setDisplayedNames(chatbotNames);

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast(
          <SuccessToast
            title="Copied!"
            detail={`${text} copied to clipboard.`}
          />,
        );
      })
      .catch((err) => {
        toast(
          <WarningToast title="Copy failed" detail="Failed to copy text." />,
        );
      });
  };

  const redirectToRegister = () => {
    window.parent.postMessage(
      { redirectUrl: 'https://app.livechatai.com/register' },
      '*',
    );
  };

  return (
    <div ref={containerRef} className="font-poppins">
      {displayedNames.length === 0 ? (
        <section id="second-step" className="py-16 sm:py-32">
          <div className="container mx-auto max-w-screen-lg px-4">
            <div className="flex flex-col items-center justify-center space-y-8">
              <div className="flex flex-col sm:flex-row sm:justify-between">
                <div className="w-full px-2 sm:w-1/2">
                  <div className="text-center text-4xl font-normal">
                    Choose the <br />
                    <span className="text-main">personality traits</span>
                  </div>
                  <div className="mt-5 text-center text-lg font-normal text-gray-700">
                    How do you want your chatbot to sound like?
                  </div>
                  <div
                    className="mt-8 flex flex-wrap justify-center gap-2"
                    data-testid="personality-traits"
                  >
                    {[
                      'Serious',
                      'Friendly',
                      'Creative',
                      'Simple',
                      'Fancy / Chic',
                      'Expert',
                      'All',
                    ].map((trait) => (
                      <button
                        key={trait}
                        type="button"
                        className={cn(
                          'hover:text-main rounded-full border border-gray-300 px-4 py-2 text-base font-normal text-gray-500 transition-all',
                          selectedTrait === trait
                            ? 'border-main text-main opacity-100 shadow-lg'
                            : '',
                        )}
                        onClick={() => handleTraitSelection(trait)}
                      >
                        {trait}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mt-4 w-full px-2 sm:mt-0 sm:w-1/2">
                  <div className="text-center text-4xl font-normal">
                    Choose the <br />{' '}
                    <span className="text-red-500">industry</span>
                  </div>
                  <div className="mt-5 text-center text-lg font-normal text-gray-700">
                    Where will the chatbot operate in?
                  </div>
                  <div
                    className="mt-8 flex flex-wrap justify-center gap-2"
                    data-testid="the-industry"
                  >
                    {[
                      'Travel',
                      'Retail',
                      'Education',
                      'Real Estate',
                      'Health Care',
                      'Legal',
                      'Finance',
                      'HR',
                      'All',
                    ].map((industry) => (
                      <button
                        key={industry}
                        type="button"
                        className={`font-satoshi rounded-full border border-gray-300 px-4 py-2 text-base font-normal text-gray-500 transition-all hover:text-red-500 ${
                          selectedIndustry === industry
                            ? 'border-red-500 text-red-500 opacity-100 shadow-lg'
                            : ''
                        }`}
                        onClick={() => handleIndustrySelection(industry)}
                      >
                        {industry}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex w-96 flex-col items-start">
                <label
                  className="text-base font-normal text-gray-700"
                  htmlFor="details"
                >
                  Provide more details
                </label>
                <Textarea
                  id="details"
                  className="mt-2 w-full rounded-lg border-gray-300"
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  maxLength={500}
                />
              </div>
              <div className="flex justify-center">
                <Button
                  data-testid="generate-button"
                  rounded="full"
                  onClick={handleSubmit}
                  variant="free-tools"
                  className="font-satoshi !w-56 !text-base font-normal text-white transition-all hover:bg-transparent hover:text-blue-500"
                  isLoading={loading}
                >
                  Generate with AI
                </Button>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <div className="mt-4 rounded-lg bg-transparent sm:mx-auto sm:w-full sm:max-w-4xl">
          <div className="my-2 rounded-lg bg-transparent px-10 py-8">
            <h3 className="mb-4 text-center text-4xl font-normal">
              {t.generatedNamesTitle}
            </h3>
            <div className="my-10 flex flex-wrap justify-center gap-2">
              {displayedNames.map((name, index) => (
                <button
                  data-testid="generated-chatbot-names"
                  key={index}
                  type="button"
                  className="font-satoshi rounded-full border border-gray-300 px-4 py-2 text-base font-normal text-gray-800 shadow-xl"
                  onClick={() => handleCopyToClipboard(name)}
                >
                  {name}
                </button>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-center">
              <Button
                data-testid="start-over-button"
                rounded="full"
                onClick={handleStartOver}
                variant="unstyled"
                className="font-satoshi border-lca_cyan bg-lca_cyan !w-36 border !px-4 !py-[6px] !text-base font-normal text-gray-800 transition-all hover:text-black hover:shadow-md"
              >
                Start Over <ArrowPathIcon className="ml-1 h-6" />
              </Button>
              {displayedNames.length < chatbotNames.length ? (
                <Button
                  data-testid="show-more-button"
                  rounded="full"
                  onClick={handleShowMeMore}
                  variant="free-tools"
                  className="font-satoshi ml-4 !w-36 !text-base font-normal transition-all hover:bg-transparent hover:text-blue-500"
                >
                  Show Me More
                </Button>
              ) : (
                <Button
                  rounded="full"
                  variant="free-tools"
                  onClick={redirectToRegister}
                  className="font-satoshi ml-4 !w-64 !text-base font-normal text-white transition-all hover:bg-transparent"
                >
                  Create Your Chatbot for Free
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatbotNameGenerator;
